import { DateTime } from "luxon";

import type {
  AllChatRoomItemType,
  AllowedChatRoomItemType,
  CustomChatRoomItem,
} from "@/Modules/Chat/Models/Domain/Chat";
import { ChatRoomItemType } from "@/Modules/Chat/Models/Domain/Chat";
import ChatRoomItemTypeGuard from "@/Modules/Chat/Models/Typeguards/ChatRoomItem";

import { dateToDateUi } from "@/Utils/Date";
import { hasValue, isObject } from "@/Utils/Objects";

const getDateDiff = (a: Date, b: Date): number => {
  const a2 = DateTime.fromJSDate(a).toFormat("D");
  const b2 = DateTime.fromJSDate(b).toFormat("D");

  return +(a2 !== b2);
};

const getFullDaysDiffFromToday = (date: Date): number => {
  const messageDate = DateTime.fromJSDate(date).startOf("day");
  const today = DateTime.local();

  return today.diff(messageDate, "days").toObject().days || 0;
};

export const getDayI18n = (date: Date): Pick<CustomChatRoomItem, "i18nKey" | "text"> => {
  const days = getFullDaysDiffFromToday(date);

  if (days < 1) {
    return { i18nKey: "general.chat.today" };
  } else if (days < 2) {
    return { i18nKey: "general.chat.yesterday" };
  } else {
    return { text: dateToDateUi(date) };
  }
};

export const getNeutral = ({
  i18nKey,
  id,
  createdAt,
  text,
}: Omit<CustomChatRoomItem, "type" | "old" | "isConfirmed" | "isReceived">): CustomChatRoomItem => ({
  type: ChatRoomItemType.NEUTRAL_TIME_TEXT,
  i18nKey,
  text,
  createdAt,
  id,
  old: false,
  isReceived: true,
});

export function addNewEventWithNeutrals(
  eventsWithNeutrals: AllChatRoomItemType[],
  event: AllChatRoomItemType
): AllChatRoomItemType[] {
  if (eventsWithNeutrals.length > 0 && isObject(event) && hasValue(event.old) && !event.old) {
    let index = eventsWithNeutrals.length - 1;
    let isPast = false;
    let duplicatedEvent: AllChatRoomItemType | undefined = undefined;

    while (index >= 0 && !isPast && !hasValue(duplicatedEvent)) {
      const currentEvent = eventsWithNeutrals[index]!;

      if (currentEvent.id === event.id) {
        duplicatedEvent = currentEvent;
      } else if (
        isObject(currentEvent) &&
        hasValue(currentEvent.createdAt) &&
        (currentEvent.createdAt as Date) < (event.createdAt as Date)
      ) {
        isPast = true;
      } else {
        index--;
      }
    }

    if (duplicatedEvent) return eventsWithNeutrals;
    if (isPast) {
      eventsWithNeutrals.splice(index + 1, 0, event);

      return [...eventsWithNeutrals];
    }
  }

  if (ChatRoomItemTypeGuard.isUnknown(event)) {
    if (!hasValue(eventsWithNeutrals) || eventsWithNeutrals.length === 0) {
      return [event];
    } else if (isObject(event) && event.old === true) {
      return [event, ...eventsWithNeutrals];
    } else {
      return [...eventsWithNeutrals, event];
    }
  }

  const lastEventIndex =
    eventsWithNeutrals === undefined || eventsWithNeutrals.length === 0
      ? -1
      : eventsWithNeutrals.findIndex((ev) => ev.type !== ChatRoomItemType.UNKNOWN);

  if (event.old) {
    if (eventsWithNeutrals === undefined || eventsWithNeutrals.length === 0) {
      return [
        getNeutral({
          ...getDayI18n(event.createdAt),
          id: `${event.id}-${event.createdAt}-date`,
          createdAt: event.createdAt,
        }),
        event,
      ];
    } else if (lastEventIndex === -1) {
      // only unknown events
      return [
        getNeutral({
          ...getDayI18n(event.createdAt),
          id: `${event.id}-${event.createdAt}-date`,
          createdAt: event.createdAt,
        }),
        event,
        ...eventsWithNeutrals,
      ];
    } else {
      // lastEvent not is unknown event
      const lastEvent = eventsWithNeutrals[lastEventIndex] as CustomChatRoomItem;

      if (getDateDiff(lastEvent.createdAt, event.createdAt) === 0) {
        lastEvent.createdAt = event.createdAt;

        if (lastEvent.type === ChatRoomItemType.NEUTRAL_TIME_TEXT) {
          lastEvent.id = `${event.id}-${event.createdAt}-date`;
        }

        eventsWithNeutrals.splice(lastEventIndex, 1);

        return [lastEvent, event, ...eventsWithNeutrals];
      } else {
        return [
          getNeutral({
            ...getDayI18n(event.createdAt),
            id: `${event.id}-${event.createdAt}-date`,
            createdAt: event.createdAt,
          }),
          event,
          ...eventsWithNeutrals,
        ];
      }
    }
  } else {
    const firstEventIndex =
      eventsWithNeutrals === undefined || eventsWithNeutrals.length === 0
        ? -1
        : eventsWithNeutrals.findLastIndex((ev) => ev.type !== ChatRoomItemType.UNKNOWN);

    if (firstEventIndex === -1) {
      return [
        ...(eventsWithNeutrals === undefined || eventsWithNeutrals.length === 0 ? [] : eventsWithNeutrals),
        getNeutral({
          ...getDayI18n(event.createdAt),
          id: `${event.id}-${event.createdAt}-date`,
          createdAt: event.createdAt,
        }),
        event,
      ];
    }

    const firstEvent = eventsWithNeutrals[firstEventIndex] as AllowedChatRoomItemType;

    if (
      eventsWithNeutrals === undefined ||
      eventsWithNeutrals.length === 0 ||
      getDateDiff(event.createdAt, firstEvent.createdAt) >= 1
    ) {
      return [
        ...eventsWithNeutrals,
        getNeutral({
          ...getDayI18n(event.createdAt),
          id: `${event.id}-${event.createdAt}-date`,
          createdAt: event.createdAt,
        }),
        event,
      ];
    } else {
      return [...eventsWithNeutrals, event];
    }
  }
}
